import React, { FC } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import {
  Pause,
  Play,
  VolumeHigh,
  VolumeOff
} from "@yleisradio/yds-icons-react";
import styled, { css } from "styled-components";

const SimpleIndicator = styled.div`
  @keyframes a {
    0% {
      opacity: 0;
      transform: scale(0.5);
    }

    50% {
      opacity: 1;
      transform: scale(0.75);
    }

    100% {
      opacity: 0;
      transform: scale(1);
    }
  }

  --radius: 54px;
  align-items: center;
  animation: a 500ms linear both;
  border-radius: 100%;
  color: ${darkTheme.TEXT_DEFAULT};
  display: flex;
  height: calc(var(--radius) * 2);
  justify-content: center;
  left: calc(50% - var(--radius));
  position: absolute;
  top: calc(50% - var(--radius));
  width: calc(var(--radius) * 2);

  svg {
    height: 50%;
    width: 50%;
  }
`;

const StyledSeekIndicator = styled.div<{ back?: boolean }>`
  padding-top: 32px;
  position: absolute;
  top: calc(50% - 14px);
  width: 76px;
  ${({ back }) =>
    back
      ? css`
          left: 25%;
        `
      : css`
          right: 25%;
        `}
`;

const Arrow = styled.div`
  @keyframes b {
    0% {
      opacity: 0;
    }

    50% {
      opacity: 1;
    }

    100% {
      opacity: 0;
    }
  }
  align-items: center;
  animation: b 500ms both;
  color: ${darkTheme.TEXT_DEFAULT};
  display: flex;
  height: 28px;
  justify-content: center;
  left: 0;
  position: absolute;
  top: 0;
  width: 20px;

  &:nth-child(2) {
    animation-delay: 200ms;
    left: 28px;
  }

  &:nth-child(3) {
    animation-delay: 400ms;
    left: 56px;
  }

  svg {
    height: 100%;
    width: 100%;
  }
`;

const Text = styled.div`
  @keyframes c {
    0% {
      opacity: 0;
    }

    30% {
      opacity: 1;
    }

    70% {
      opacity: 1;
    }

    100% {
      opacity: 0;
    }
  }
  animation: c 900ms both;
  color: ${darkTheme.TEXT_DEFAULT};
  text-align: center;
  text-shadow: 0 0 4px ${darkTheme.TEXT_NEGATIVE};
`;

const ArrowContainer = styled.div<{ back?: boolean }>`
  ${({ back }) =>
    back &&
    css`
      transform: rotate(180deg);
    `}
`;

const SeekIndicator = ({ back }: { back?: boolean }) => (
  <StyledSeekIndicator back={back}>
    <ArrowContainer back={back}>
      <Arrow>
        <Play />
      </Arrow>
      <Arrow>
        <Play />
      </Arrow>
      <Arrow>
        <Play />
      </Arrow>
    </ArrowContainer>
    <Text>10 s</Text>
  </StyledSeekIndicator>
);

interface Props {
  icon: "back" | "forward" | "mute" | "pause" | "play" | "unmute" | null;
}

export const ActionIndicator: FC<Props> = ({ icon }) => {
  switch (icon) {
    case "back":
      return <SeekIndicator back={true} />;
    case "forward":
      return <SeekIndicator />;
    case "mute":
      return (
        <SimpleIndicator>
          <VolumeOff />
        </SimpleIndicator>
      );
    case "unmute":
      return (
        <SimpleIndicator>
          <VolumeHigh />
        </SimpleIndicator>
      );
    case "pause":
      return (
        <SimpleIndicator>
          <Pause />
        </SimpleIndicator>
      );
    case "play":
      return (
        <SimpleIndicator>
          <Play />
        </SimpleIndicator>
      );
    default:
      return null;
  }
};
